import React from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { LayoutDashboard, Image, Tag, Bell, AlertTriangle, RotateCcw, Users, Package, UserCog, RefreshCw, ShoppingCart, Link2, Settings2, Mail, ClipboardCheck, Percent, ShieldCheck, Briefcase } from 'lucide-react';
import { BuildVersionBadge } from '../../components/admin/BuildVersionBadge';
import { useAdminNotificationsOptional } from '../../context/AdminNotificationsContext';
import { useAuth } from '../../context/AuthContext';
import type { AdminPermission } from '../../types';

type NavItem = { icon: any; label: string; href: string; notifBadge?: true; permission?: AdminPermission; adminOnly?: true } | null;

// ``permission`` assente = visibile a QUALSIASI utente del pannello admin
// (vero admin o admin_operatore, es. Dashboard/Notifiche — solo info di
// base, richiesta utente 25/02/2026). ``adminOnly: true`` = visibile SOLO
// al vero admin, in NESSUN caso delegabile (Team Amministrativo — gestione
// dei poteri altrui, mai concedibile a un admin_operatore anche se ha
// selezionato tutti gli altri poteri).
const ADMIN_NAV: NavItem[] = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/admin' },
  { icon: Bell, label: 'Notifiche', href: '/admin/notifiche', notifBadge: true },
  // "Banner homepage" e non più "Banner & Promo": la Striscia promo
  // (PromoBanner.tsx) non si gestisce da qui e tornerà separatamente come
  // slot "topbar". L'etichetta vecchia era la fonte dell'ambiguità.
  { icon: Image, label: 'Banner homepage', href: '/admin/banner', permission: 'contenuti' },
  { icon: LayoutDashboard, label: 'Homepage Prodotti', href: '/admin/homepage-prodotti', permission: 'contenuti' },
  { icon: Briefcase, label: 'Carriere', href: '/admin/carriere', permission: 'carriere' },
  null,
  { icon: Tag, label: 'Prodotti', href: '/admin/prodotti', permission: 'catalogo' },
  { icon: Package, label: 'Alert Stock', href: '/admin/alert-stock', permission: 'catalogo' },
  null,
  { icon: RotateCcw, label: 'RMA', href: '/admin/rma', permission: 'resi_ordini' },
  { icon: ShoppingCart, label: 'Ordini', href: '/admin/ordini', permission: 'resi_ordini' },
  { icon: ShoppingCart, label: 'Carrelli', href: '/admin/carrelli', permission: 'resi_ordini' },
  null,
  { icon: Users, label: 'Clienti', href: '/admin/clienti', permission: 'clienti' },
  { icon: ClipboardCheck, label: 'Registrazioni', href: '/admin/registrazioni', permission: 'clienti' },
  { icon: Percent, label: 'Condizioni Commerciali', href: '/admin/condizioni-commerciali', permission: 'clienti' },
  { icon: UserCog, label: 'Impersonazione', href: '/admin/impersona', permission: 'clienti' },
  null,
  { icon: Link2, label: 'Abbinatore', href: '/admin/abbinatore-v2', permission: 'abbinatore' },
  null,
  { icon: AlertTriangle, label: 'Segnalazioni', href: '/admin/difformita', permission: 'segnalazioni' },
  null,
  { icon: RefreshCw, label: 'Aggiornamenti', href: '/admin/sync-erp', permission: 'sistema' },
  { icon: Settings2, label: 'Configurazione', href: '/admin/configurazione', permission: 'sistema' },
  { icon: Mail, label: 'Email & Notifiche', href: '/admin/email', permission: 'sistema' },
  null,
  { icon: ShieldCheck, label: 'Team Amministrativo', href: '/admin/team', adminOnly: true },
];

/** Filtra la sidebar per un Team Amministrativo con poteri limitati
 * (richiesta utente 25/02/2026): un item è visibile se non ha ``permission``
 * (info di base, sempre visibile) OPPURE se l'operatore ha quel potere —
 * MAI se ``adminOnly: true`` (Team, sempre riservato al vero admin). */
function filterNavForOperatore(nav: NavItem[], permessi: AdminPermission[]): NavItem[] {
  const filtered = nav.filter(item => item === null || (!item.adminOnly && (!item.permission || permessi.includes(item.permission))));
  // Rimuove separatori duplicati/orfani (in testa, in coda, o consecutivi)
  const cleaned: NavItem[] = [];
  filtered.forEach((item, i) => {
    if (item === null) {
      if (i === 0 || i === filtered.length - 1 || cleaned[cleaned.length - 1] === null) return;
    }
    cleaned.push(item);
  });
  return cleaned;
}

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const notifCtx = useAdminNotificationsOptional();
  const adminUnread = notifCtx?.data?.unreadCount ?? 0;
  const { user } = useAuth();
  const isLimitedOperatore = user?.ruolo === 'admin_operatore';
  const nav = isLimitedOperatore ? filterNavForOperatore(ADMIN_NAV, user?.permessiAdmin || []) : ADMIN_NAV;

  return (
    <div className="flex min-h-[calc(100vh-56px-40px)]" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-56 border-r bg-white flex-shrink-0 hidden lg:flex flex-col">
        <div className="p-4 border-b">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-red-500 flex items-center justify-center">
              <span className="text-white font-bold text-[10px]">BO</span>
            </div>
            <div>
              <div className="text-sm font-semibold">Backoffice DTK</div>
              <div className="text-[10px] text-muted-foreground">Pannello Operativo</div>
            </div>
          </div>
        </div>
        <nav className="p-2 space-y-0.5 flex-1 overflow-y-auto">
          {nav.map((item, i) => {
            if (!item) return <div key={`sep-${i}`} className="h-px bg-border my-2" />;
            const isActive = location.pathname === item.href || (item.href !== '/admin' && location.pathname.startsWith(item.href));
            return (
              <Link key={item.href} to={item.href}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-red-50 text-red-700 font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                }`}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1">{item.label}</span>
                {item.notifBadge && adminUnread > 0 && (
                  <span className="min-w-[18px] px-1 h-[18px] rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid="admin-sidebar-notif-badge">
                    {adminUnread > 99 ? '99+' : adminUnread}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t space-y-1.5">
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground block">Torna al sito</Link>
          <div className="pt-2 border-t border-border/50">
            <BuildVersionBadge />
          </div>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 min-w-0 bg-[hsl(var(--dtk-canvas))]">
        {children}
      </div>
    </div>
  );
}
